/**
 * Open the lesson a class was launched into, as its host.
 *
 * This existed three times: once in TeacherDashboard, once in PartnerDashboard,
 * and — correctly — as `buildActiveLesson`, which the student router already
 * uses. The two dashboard copies re-implemented the bundle→payload shaping by
 * hand and had fallen behind: neither carried `images`, so lesson images were
 * simply absent for any teacher who entered through them, while the same lesson
 * showed them to every student.
 *
 * One copy, so the host and the class cannot disagree about what a lesson is.
 */

import { getVr360TourById, VR360_TOUR_CHAPTER_ID } from '../../config/vr360Tours';
import type { LaunchedLesson } from '../../types/lms';
import { buildActiveLesson, type ActiveLessonPayload } from '../lesson/buildActiveLesson';
import { openLessonInPlayer } from './resolvePlayerRoute';

export type OpenAsHostFailure = 'no-launch' | 'tour-missing' | 'lesson-unavailable';

export interface OpenLaunchedLessonAsHostOptions {
  launched: LaunchedLesson | null | undefined;
  sessionId: string | null | undefined;
  navigate: (to: string, options?: { replace?: boolean }) => void;
  /**
   * Run with the payload just before navigating.
   *
   * The partner dashboard starts LessonContext here and the teacher one does
   * not. That difference is real, so it stays with the caller rather than being
   * quietly normalised into one behaviour or the other.
   */
  onLessonReady?: (payload: ActiveLessonPayload) => void;
}

export interface OpenAsHostResult {
  ok: boolean;
  reason?: OpenAsHostFailure;
}

function isVr360(launched: LaunchedLesson): boolean {
  return launched.lesson_type === 'vr360_video' || launched.chapter_id === VR360_TOUR_CHAPTER_ID;
}

/**
 * Navigate the host into the launched lesson.
 *
 * Returns a result instead of throwing or toasting: each caller already has its
 * own loading flag and its own wording for the failure.
 */
export async function openLaunchedLessonAsHost({
  launched,
  sessionId,
  navigate,
  onLessonReady,
}: OpenLaunchedLessonAsHostOptions): Promise<OpenAsHostResult> {
  if (!launched || !sessionId) return { ok: false, reason: 'no-launch' };

  if (isVr360(launched)) {
    const tourId =
      launched.vr360_tour_id ||
      (typeof launched.topic_id === 'string' && launched.topic_id.startsWith('tour-')
        ? launched.topic_id.replace(/^tour-/, '')
        : null);
    const tour = tourId ? getVr360TourById(tourId) : undefined;
    if (!tour) return { ok: false, reason: 'tour-missing' };

    try {
      sessionStorage.setItem(
        'learnxr_vr360_tour',
        JSON.stringify({
          tourId: tour.id,
          title: tour.title,
          videoPath: tour.videoPath,
          videoStoragePath: tour.videoStoragePath,
          player: tour.player,
          fromClassSession: true,
        })
      );
      sessionStorage.setItem('learnxr_class_session_id', sessionId);
    } catch (storageError) {
      // Private mode and blocked site data both land here. The tour screen reads
      // these keys, so there is nothing useful to navigate to without them.
      console.error('[openLaunchedLessonAsHost] Could not stage the 360° tour:', storageError);
      return { ok: false, reason: 'tour-missing' };
    }

    navigate('/vr360-videotour');
    return { ok: true };
  }

  try {
    const payload = await buildActiveLesson(launched);
    if (!payload) return { ok: false, reason: 'lesson-unavailable' };

    sessionStorage.setItem('activeLesson', JSON.stringify(payload));
    onLessonReady?.(payload);

    // The host must land in the same player as the class they are driving.
    openLessonInPlayer(navigate, { launched });
    return { ok: true };
  } catch (error) {
    console.error('[openLaunchedLessonAsHost] Failed to open the launched lesson:', error);
    return { ok: false, reason: 'lesson-unavailable' };
  }
}
